import React, { useState, useContext, useEffect } from 'react';
import {
  getTranslatedText,
  IndicatorSeparator,
  DropdownIndicator,
  Suffix,
  withErrorBoundary,
  getDateTimeFormat
} from '@nile/components';
import {
  reactSelectErrorStyles,
  reactSelectInlineError,
  BadgeWrapper
} from '@nile/banyan/src/Utils/style';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import { AccountClient, GET_SERVICE_ITEMS } from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import { cloneDeep, pick, omit, get } from 'lodash';
import dayjs from 'dayjs';
import { useQuery } from '@apollo/client';
import ServiceSheet from '@nile/client_management/src/Pages/ContractTemplate/ServiceSheet';
import { FormattedMessage, injectIntl } from 'react-intl';
import NewScheduleDurationSection from './NewScheduleDuration';
import Select from 'react-select';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SVGIcon } from '@superops/super-icons';
import { Dropdown, TextStyle, TextField } from '@superops/ui-design-system';
import {
  SCHEDULE_OPTIONS,
  WEEK_OPTIONS,
  DAY_OPTIONS,
  MONTH_DAY_OPTIONS,
  HOUR_OPTIONS,
  MEREDIAN_OPTIONS,
  END_OPTION,
  SCHEDULE_ENDTYPE
} from './constants';

function ScheduleDuration(props) {
  const flags = useFlags();
  if (flags.newScheduleDuration) {
    return <NewScheduleDurationSection {...props} />;
  }
  return <ScheduleDurationSection {...props} />;
}

function ScheduleDurationSection(props) {
  const {
    intl,
    duration,
    filteredOptions,
    allowTime,
    scheduleMessage,
    disableInterval,
    errors,
    showOccurance = false,
    showInterval = false,
    occurancei18nMap,
    readOnly = false,
    hideStartEndDatetime,
    showTimePicker,
    noPadding,
    maxLimits = {},
    validation = {}
  } = props;

  const [time, setTime] = useState(null);
  const { frequency, data } = duration;
  const { dateFormat, MSPDateFormat, timeFormat } = getDateTimeFormat();

  useEffect(() => {
    if (data?.time) {
      const updatedTime = new Date();
      const [hrs, mins] = data?.time.split(':');
      updatedTime.setHours(hrs);
      updatedTime.setMinutes(mins);
      setTime(updatedTime || null);
    }
  }, [data?.time]);

  const FILTERED_SCHEDULE_OPTIONS = filteredOptions
    ? SCHEDULE_OPTIONS.filter((opt) => filteredOptions.includes(opt.value))
    : SCHEDULE_OPTIONS;

  function getMinValValidationInfo(
    options,
    validationType = 'schedule_options'
  ) {
    return get(validation, `${validationType}.${options?.value}.minValue`, '1');
  }
  const handleDurationChange = (field, value) => {
    if (value === null) return;
    let updatedDuration = cloneDeep(duration);
    const objToUpdate = {};
    const val = {};
    let updatedTime = '';
    let timeValue = '';
    switch (field) {
      case 'frequency':
        if (frequency !== value.value) {
          updatedDuration = {
            frequency: value.value,
            data: {
              repeatInterval:
                getMinValValidationInfo(value, 'schedule_options') ||
                updatedDuration.data.repeatInterval,
              endType: updatedDuration.data.endType,
              time: updatedDuration.data.time,
              ...(updatedDuration.data.endDate ||
              updatedDuration.data.endDateAndTime
                ? allowTime
                  ? { endDateAndTime: updatedDuration.data.endDateAndTime }
                  : { endDate: updatedDuration.data.endDate }
                : {}),
              ...(updatedDuration.data.startDate ||
              updatedDuration.data.startDateAndTime
                ? allowTime
                  ? { startDateAndTime: updatedDuration.data.startDateAndTime }
                  : { startDate: updatedDuration.data.startDate }
                : {})
            }
          };
        }
        break;
      case 'repeatInterval':
        updatedDuration = {
          ...updatedDuration,
          data: {
            ...updatedDuration.data,
            repeatInterval: value
          }
        };
        break;
      case 'endType':
        updatedDuration =
          value.value === 'never'
            ? {
                ...updatedDuration,
                data: {
                  ...updatedDuration.data,
                  endType: value.value,
                  endDate: null,
                  endDateAndTime: null,
                  repeatCount: null
                }
              }
            : {
                ...updatedDuration,
                data: {
                  ...updatedDuration.data,
                  endType: value.value
                }
              };
        break;
      case 'repeatCount':
        updatedDuration = {
          ...updatedDuration,
          data: {
            ...updatedDuration.data,
            startDate: null,
            repeatCount: value
          }
        };
        break;
      case 'time':
        function padTo2Digits(num) {
          return String(num).padStart(2, '0');
        }

        const date = value;
        const hoursAndMinutes = `${padTo2Digits(
          date.getHours()
        )}:${padTo2Digits(date.getMinutes())}`;

        updatedDuration = {
          ...updatedDuration,
          data: {
            ...updatedDuration.data,
            time: hoursAndMinutes
          }
        };
        break;
      case 'meridian':
        timeValue = Number(updatedDuration.data.time.split(':')[0]);
        updatedTime =
          value.value === 'AM'
            ? timeValue > 12
              ? `${timeValue - 12}:${updatedDuration.data.time.split(':')[1]}`
              : updatedDuration.data.time
            : timeValue < 12
            ? `${timeValue + 12}:${updatedDuration.data.time.split(':')[1]}`
            : updatedDuration.data.time;
        updatedDuration = {
          ...updatedDuration,
          data: {
            ...updatedDuration.data,
            time: updatedTime
          }
        };
        break;
      case 'type':
        objToUpdate.type = value.type;
        if (value.type === 'DAY_OF_MONTH') {
          objToUpdate.dayOfMonth = value.value;
        }
        if (value.type === 'WEEK_OF_MONTH') {
          objToUpdate.week = value.value;
        }
        updatedDuration = {
          ...updatedDuration,
          data: {
            // time: updatedDuration.data.time,
            // startDate: updatedDuration.data.startDate,
            ...updatedDuration.data,
            ...objToUpdate
          }
        };
        break;
      case 'day':
        updatedDuration = {
          ...updatedDuration,
          data: {
            ...updatedDuration.data,
            dayOfWeek: value.value
          }
        };
        break;
      case 'date':
        updatedDuration = {
          ...updatedDuration,
          data: {
            ...updatedDuration.data,
            startDate: dayjs(value).format('YYYY-MM-DD')
          }
        };
        break;
      case 'endDate':
        updatedDuration = {
          ...updatedDuration,
          data: {
            ...updatedDuration.data,
            endDate: dayjs(value).format('YYYY-MM-DD')
          }
        };
        break;
      case 'startDateAndTime':
        updatedDuration = {
          ...updatedDuration,
          data: {
            ...updatedDuration.data,
            startDateAndTime: dayjs(value).format('YYYY-MM-DDTHH:mm:ss'),
            startDate: null,
            repeatCount: null
          }
        };
        break;
      case 'endDateAndTime':
        updatedDuration = {
          ...updatedDuration,
          data: {
            ...updatedDuration.data,
            endDateAndTime: dayjs(value).format('YYYY-MM-DDTHH:mm:ss'),
            endDate: null,
            repeatCount: null
          }
        };
        break;

      case 'noOfTimes':
        updatedDuration = {
          ...updatedDuration,
          data: {
            ...updatedDuration.data,
            noOfTimes: value,
            endDate: null,
            endDateAndTime: null
          }
        };
      default:
        break;
    }

    props.updateParent(updatedDuration);
  };
  // const timeValue = data.time ? Number(data.time.split(':')[0]) : '';
  // const displayTime =
  //   timeValue > 12 ? `${timeValue - 12}:${data.time.split(':')[1]}` : data.time;

  const weekOptions = WEEK_OPTIONS.map((el) => ({
    ...el,
    label: el.value === 'LAST' ? getTranslatedText(intl, el.label) : el.label
  }));

  const monthDayOptions = MONTH_DAY_OPTIONS.map((el) => ({
    ...el,
    label: el.value === '0' ? getTranslatedText(intl, el.label) : el.label
  }));

  const dayOptions = DAY_OPTIONS.map((el) => {
    return {
      ...el,
      label: getTranslatedText(intl, el.label)
    };
  });

  const groupedOptions = [
    {
      label: 'Week options',
      options: weekOptions
    },
    {
      label: 'Day options',
      options: monthDayOptions
    }
  ];
  const selectedTypeOfDuration =
    data.type === 'DAY_OF_MONTH'
      ? MONTH_DAY_OPTIONS.map((el) => ({
          ...el,
          label: el.value === '0' ? getTranslatedText(intl, el.label) : el.label
        })).find((el) => el.value === data.dayOfMonth)
      : WEEK_OPTIONS.map((el) => ({
          ...el,
          label:
            el.value === 'LAST' ? getTranslatedText(intl, el.label) : el.label
        })).find((el) => el.value === data.week);
  let selectedDay = DAY_OPTIONS.find((el) => el.value === data.dayOfWeek);
  selectedDay = selectedDay
    ? {
        ...selectedDay,
        label: getTranslatedText(intl, selectedDay.label)
      }
    : null;

  const dayOption = MONTH_DAY_OPTIONS.map((el) => ({
    ...el,
    label: el.value === '0' ? getTranslatedText(intl, el.label) : el.label
  }));

  const endOption = END_OPTION.map((option) => ({
    ...option,
    label: getTranslatedText(intl, option.label)
  }));

  const scheduleDropdownOptions = FILTERED_SCHEDULE_OPTIONS.map((el) => ({
    ...el,
    label: getTranslatedText(intl, el.label)
  }));
  const scheduleOptionsValue = scheduleDropdownOptions.find(
    (el) => el.value === frequency
  );

  const scheduleOptionMinVal = getMinValValidationInfo(
    scheduleOptionsValue,
    'schedule_options'
  );
  return (
    <Wrapper>
      <div className={`flex center relative ${!noPadding && 'mb-20'}`}>
        <div className="mr-10">
          <FormattedMessage id={scheduleMessage || 'schedule.info.message'} />
        </div>
        <div className="mr-10">
          <SVGIcon name="EstimatedTime" />
        </div>
        <TextField
          value={data.repeatInterval?.toString()}
          onChange={(value) => handleDurationChange('repeatInterval', value)}
          customClassName="w50 text-input"
          type="number"
          min={1} // If the min value is a two-digit number (10 or more), the input field unable to clear
          max={maxLimits[frequency]}
          onBlur={(e) => {
            if (Number(e.target.value) < scheduleOptionMinVal)
              handleDurationChange('repeatInterval', scheduleOptionMinVal);
            if (Number(e.target.value) > maxLimits[frequency])
              handleDurationChange('repeatInterval', maxLimits[frequency]);
          }}
          placeholder={
            scheduleOptionMinVal ||
            getTranslatedText(intl, 'schedule.interval.placeholder')
          }
          disabled={disableInterval || readOnly}
          error={errors?.repeatInterval}
        />
        <Select
          className="w100p unit-selector"
          classNamePrefix="value_selector"
          value={scheduleOptionsValue}
          components={{ IndicatorSeparator, DropdownIndicator }}
          onChange={(value) => handleDurationChange('frequency', value)}
          options={scheduleDropdownOptions}
          placeholder="0"
          menuPortalTarget={document.body}
          menuPlacement="auto"
          isSearchable={false}
          isDisabled={readOnly}
          closeMenuOnScroll={(e) => {
            return (
              e.target &&
              e.target.classList &&
              !(e.target.classList.value.indexOf('menu-list') > -1)
            );
          }}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, zIndex: 9999 }),
            control: (base) => ({ ...base, width: 50 })
          }}
        />

        <div className="ml-10 mr-10">
          {frequency === 'HOURLY' || frequency === 'DAILY' ? (
            <FormattedMessage id="schedule.info.message.once" />
          ) : showOccurance ? (
            occurancei18nMap?.conjunctionText && (
              <FormattedMessage id={occurancei18nMap?.conjunctionText} />
            )
          ) : (
            <FormattedMessage id="schedule.info.message.onceon" />
          )}
        </div>
        {showOccurance && (frequency === 'MINUTES' || frequency === 'HOURLY') && (
          <div className="fx center">
            <div className="fx actr">
              <TextField
                value={data?.noOfTimes?.toString()}
                onChange={(value) => handleDurationChange('noOfTimes', value)}
                customClassName="w120 occurance-input"
                type="number"
                placeholder={getTranslatedText(
                  intl,
                  occurancei18nMap.placeholder
                )}
                disabled={readOnly}
                error={errors?.noOfTimes}
              />
              <BadgeWrapper marginLeft="-50px" marginTop="5px">
                <FormattedMessage id="schedule.occurance.badge" />
              </BadgeWrapper>
            </div>
            <div className="ml-10">
              <FormattedMessage id={occurancei18nMap.helpText} />
            </div>
          </div>
        )}

        {frequency === 'MONTHLY' && (
          <Select
            className=""
            classNamePrefix="value_selector"
            value={selectedTypeOfDuration}
            components={{ IndicatorSeparator, DropdownIndicator }}
            onChange={(value) => handleDurationChange('type', value)}
            options={groupedOptions}
            placeholder={getTranslatedText(
              intl,
              'schedule.duration.options.placeholder'
            )}
            isDisabled={readOnly}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            closeMenuOnScroll={(e) => {
              return (
                e.target &&
                e.target.classList &&
                !(e.target.classList.value.indexOf('menu-list') > -1)
              );
            }}
            styles={
              errors?.type
                ? {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({ ...base, width: 50 }),
                    ...reactSelectErrorStyles
                  }
                : {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({ ...base, width: 50 })
                  }
            }
          />
        )}
        {(data.type === 'WEEK_OF_MONTH' || frequency === 'WEEKLY') && (
          <Select
            className="ml-10"
            classNamePrefix="value_selector"
            value={selectedDay}
            isDisabled={readOnly}
            components={{ IndicatorSeparator, DropdownIndicator }}
            onChange={(value) => handleDurationChange('day', value)}
            options={dayOptions}
            placeholder={getTranslatedText(
              intl,
              'invoiceSettings.weekOfMonth.duration.placeholder'
            )}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            closeMenuOnScroll={(e) => {
              return (
                e.target &&
                e.target.classList &&
                !(e.target.classList.value.indexOf('menu-list') > -1)
              );
            }}
            styles={
              errors?.dayOfWeek
                ? {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({ ...base, width: 50 }),
                    ...reactSelectErrorStyles
                  }
                : {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({ ...base, width: 50 })
                  }
            }
          />
        )}
        {showTimePicker && (
          <div className="ml-10">
            <DatePicker
              selected={time}
              onChange={(value) => handleDurationChange('time', value)}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              timeCaption="Time"
              disabled={readOnly}
              wrapperClassName={errors?.time ? 'field-error' : ''}
              dateFormat={timeFormat} // If showTimePicker is true, dateFormat will be timeFormat
              timeFormat={timeFormat}
              placeholderText={getTranslatedText(
                intl,
                'asset.runscript.script.time.placeholder'
              )}
            />
          </div>
        )}
      </div>

      {!hideStartEndDatetime && (
        <div className="flex center">
          {!!frequency && !showOccurance && (
            <div className="datepicker-wrapper flex">
              <div className="mr-10">
                <FormattedMessage id="client.details.contract.scheduleStartingFrom" />
              </div>
              <div className="mr-10">
                <SVGIcon name="DatePicker" />
              </div>
              <DatePicker
                selected={
                  allowTime
                    ? data.startDateAndTime &&
                      dayjs(data.startDateAndTime).isValid() &&
                      dayjs(data.startDateAndTime).toDate()
                    : data.startDate &&
                      dayjs(data.startDate).isValid() &&
                      dayjs(data.startDate).toDate()
                }
                onChange={(value) =>
                  allowTime
                    ? handleDurationChange('startDateAndTime', value)
                    : handleDurationChange('date', value)
                }
                placeholderText={
                  allowTime ? `${MSPDateFormat}, ${timeFormat}` : MSPDateFormat
                }
                dateFormat={
                  allowTime ? `${dateFormat}, ${timeFormat}` : dateFormat
                }
                timeFormat={timeFormat}
                required
                disabled={readOnly}
                showTimeSelect={allowTime}
                timeIntervals={15}
                popperPlacement="auto"
                onKeyDown={(e) => e.preventDefault()}
              />
            </div>
          )}
          {showInterval && (
            <div className="flex actr">
              <div className="mr-10">
                <TextStyle>Ends</TextStyle>
              </div>
              <div className="mr-10">
                <SVGIcon name="Clock" />
              </div>
              <Select
                className="w100p"
                classNamePrefix="value_selector"
                value={endOption.find((el) => el.value === data.endType)}
                onChange={(selectedIndex) =>
                  handleDurationChange('endType', selectedIndex)
                }
                name="valueselector"
                components={{ IndicatorSeparator, DropdownIndicator }}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, zIndex: 9999 })
                }}
                options={endOption}
                isDisabled={readOnly}
              />
              {data.endType !== SCHEDULE_ENDTYPE.NEVER && (
                <>
                  <div className="fx ml-10">
                    {data.endType === SCHEDULE_ENDTYPE.AFTERNRUNS && (
                      <>
                        <div className="fx mlr-10 actr">
                          <SVGIcon name="Reboot" />
                        </div>
                        <TextField
                          customClassName="w50 text-input"
                          type="number"
                          min={1}
                          onBlur={(e) =>
                            Number(e.target.value) < 1 &&
                            handleDurationChange('repeatCount', '1')
                          }
                          disabled={readOnly}
                          placeholder={
                            '1' ||
                            getTranslatedText(
                              intl,
                              'schedule.interval.placeholder'
                            )
                          }
                          onChange={(value) =>
                            handleDurationChange('repeatCount', value)
                          }
                          value={data.repeatCount?.toString()}
                        />
                        <Suffix width="50px">
                          <FormattedMessage id="admin.policyManagement.patch.reboot.loggedInUser.placeholder" />
                        </Suffix>
                      </>
                    )}
                    {data.endType === SCHEDULE_ENDTYPE.AFTERDATETIME && (
                      <>
                        <div className="fx mlr-10 actr">
                          <SVGIcon name="Reboot" />
                        </div>
                        <DatePicker
                          selected={
                            allowTime
                              ? data.endDateAndTime &&
                                dayjs(data?.endDateAndTime).isValid() &&
                                dayjs(data?.endDateAndTime).toDate()
                              : data.endDate &&
                                dayjs(data.endDate).isValid() &&
                                dayjs(data.endDate).toDate()
                          }
                          onChange={(value) =>
                            allowTime
                              ? handleDurationChange('endDateAndTime', value)
                              : handleDurationChange('endDate', value)
                          }
                          placeholderText={
                            allowTime
                              ? `${MSPDateFormat}, ${timeFormat}`
                              : MSPDateFormat
                          }
                          dateFormat={
                            allowTime
                              ? `${dateFormat}, ${timeFormat}`
                              : dateFormat
                          }
                          timeFormat={timeFormat}
                          required
                          disabled={readOnly}
                          showTimeSelect={allowTime}
                          timeIntervals={15}
                          popperPlacement="auto"
                          onKeyDown={(e) => e.preventDefault()}
                        />
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
      {errors?.startDateAndTime &&
        reactSelectInlineError(
          // getTranslatedText(intl, 'reports.share.validation.subject')
          getTranslatedText(
            intl,
            errors?.startDateAndTime?.[0] || 'schedule.settings.error.startDate'
          )
        )}
      {errors?.startDate &&
        reactSelectInlineError(
          getTranslatedText(intl, 'schedule.settings.error.startDate')
        )}
      {errors?.endDateAndTime &&
        reactSelectInlineError(
          getTranslatedText(intl, 'schedule.settings.error.endDateTime')
        )}
      {errors?.repeatCount &&
        reactSelectInlineError(
          getTranslatedText(intl, 'schedule.settings.error.repeatCount')
        )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  .value_selector__control {
    width: 150px;
  }
  .w100p {
    .value_selector__control {
      width: 100px;
    }
  }
  .unit-selector {
    .value_selector__control {
      background: #c4c4c433;
      border: 1px solid #c4c4c433 !important;
      border-radius: 10px 10px 0 0 !important;
    }
  }
  .w50 {
    width: 50px;
  }
  .w120 {
    width: 120px;
  }
  .occurance-input {
    height: 38px;
    margin-bottom: 0px !important;
  }
  .text-input {
    height: 38px;
    margin-bottom: 0px !important;
  }
  .datepicker-wrapper {
    input {
      outline: none;
    }
  }
`;

export default withErrorBoundary(injectIntl(ScheduleDuration));
